import { useState, type ReactNode } from 'react'
import type { TableController } from '@v2/table'
import type { CameraPlace } from '@v2/contracts'
import { CameraPlaces } from './CameraPlaces'
import './camera-controls.css'

/** A single quiet row of camera buttons. Directional pads live behind one disclosure. */
export function CameraControls({ controller, mode, places = [] }: { controller: TableController | null; mode: 'orbit' | 'pan'; places?: readonly CameraPlace[] }) {
  const [expanded, setExpanded] = useState(false)
  const button = (label: string, action: () => void, children: ReactNode, extra?: string) => <button type="button" className={extra} aria-label={label} title={label} onClick={action}>{children}</button>
  return <div className="tt-camera" role="group" aria-label="Camera controls">
    {expanded && <div className="tt-camera-details">
      <div className="tt-camera-navigation">
        <div className="tt-camera-pad" aria-label="Pan view">
          {button('Pan view up', () => controller?.panCamera(0, 65), <CameraGlyph kind="up"/>, 'is-up')}
          {button('Pan view left', () => controller?.panCamera(65, 0), <CameraGlyph kind="left"/>, 'is-left')}
          <CameraGlyph kind="pan"/>
          {button('Pan view right', () => controller?.panCamera(-65, 0), <CameraGlyph kind="right"/>, 'is-right')}
          {button('Pan view down', () => controller?.panCamera(0, -65), <CameraGlyph kind="down"/>, 'is-down')}
        </div>
        <div className="tt-camera-pad" aria-label="Rotate view">
          {button('Tilt view up', () => controller?.orbitCamera(0, 35), <CameraGlyph kind="up"/>, 'is-up')}
          {button('Rotate view left', () => controller?.orbitCamera(-55, 0), <CameraGlyph kind="left"/>, 'is-left')}
          <CameraGlyph kind="orbit"/>
          {button('Rotate view right', () => controller?.orbitCamera(55, 0), <CameraGlyph kind="right"/>, 'is-right')}
          {button('Tilt view down', () => controller?.orbitCamera(0, -35), <CameraGlyph kind="down"/>, 'is-down')}
        </div>
      </div>
      <p>Right-drag, Shift-drag or Space-drag pans. Two fingers pan and pinch. Keyboard: WASD pan, arrows orbit, + / − zoom, T top, Home fit.</p>
    </div>}
    <div className="tt-camera-bar">
      <CameraPlaces places={places} onNavigate={id => controller?.goToPlace(id)}/>
      <button type="button" aria-label="Camera orbit mode" title="Drag empty table to orbit" aria-pressed={mode === 'orbit'} onClick={() => controller?.setCameraMode('orbit')}><CameraGlyph kind="orbit"/></button>
      <button type="button" aria-label="Camera pan mode" title="Drag to pan the table" aria-pressed={mode === 'pan'} onClick={() => controller?.setCameraMode('pan')}><CameraGlyph kind="pan"/></button>
      <span className="tt-camera-divider"/>
      {button('Zoom out', () => controller?.zoomCamera(180), <CameraGlyph kind="minus"/>)}
      {button('Zoom in', () => controller?.zoomCamera(-180), <CameraGlyph kind="plus"/>)}
      <span className="tt-camera-divider"/>
      {button('Top view', () => controller?.topCamera(), <CameraGlyph kind="top"/>)}
      {button('Camera overview', () => controller?.resetCamera(), <CameraGlyph kind="fit"/>)}
      <button type="button" aria-label="More camera controls" title="Pan, rotate and keyboard shortcuts" aria-expanded={expanded} onClick={() => setExpanded(value => !value)}><CameraGlyph kind={expanded ? 'close' : 'more'}/></button>
    </div>
  </div>
}

type Glyph = 'orbit' | 'pan' | 'plus' | 'minus' | 'top' | 'fit' | 'more' | 'close' | 'up' | 'down' | 'left' | 'right'
function CameraGlyph({ kind }: { kind: Glyph }) {
  const paths: Record<Glyph, ReactNode> = {
    orbit: <><path d="M19 8a8 8 0 1 0 0 8M20 3v6h-6"/><path d="m9 9 6 3-6 3Z"/></>,
    pan: <><path d="M12 3v18M3 12h18m-12-6 3-3 3 3m-6 12 3 3 3-3M6 9l-3 3 3 3m12-6 3 3-3 3"/></>,
    plus: <path d="M5 12h14M12 5v14"/>, minus: <path d="M5 12h14"/>,
    top: <><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M8 8h8v8H8Z"/></>,
    fit: <><path d="M3 9V3h6m6 0h6v6M3 15v6h6m6 0h6v-6"/><path d="M8 8h8v8H8Z"/></>,
    more: <><circle cx="5" cy="12" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/></>,
    close: <path d="m6 6 12 12M6 18 18 6"/>,
    up: <path d="m6 14 6-6 6 6"/>, down: <path d="m6 10 6 6 6-6"/>,
    left: <path d="m14 6-6 6 6 6"/>, right: <path d="m10 6 6 6-6 6"/>,
  }
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[kind]}</svg>
}
